import React, { useState, useEffect, useRef } from 'react';
import { Member } from '../types';
import { User, Plus, Check, X, Users, Settings, Trash2, ArrowLeft, Camera, Upload, Loader2 } from 'lucide-react';
import { COLORS } from '../constants';
import { compressImage } from '../utils/imageCompression';
import { uploadAvatarImage, getCurrentUser, getCheckInImageUrl, deleteCheckInImage } from '../services/supabaseClient';

interface MemberSwitcherProps {
  members: Member[];
  currentMemberId: string;
  onSwitch: (id: string) => void;
  onAddMember: (name: string, color: string, birthDate?: string, gender?: 'male' | 'female', avatarEmoji?: string, avatarImage?: string) => void;
  onUpdateMember: (id: string, name: string, color: string, birthDate?: string, gender?: 'male' | 'female', avatarEmoji?: string, avatarImage?: string) => void;
  onDeleteMember: (id: string) => void;
}

const AVATAR_EMOJIS = ['😀', '😎', '🥳', '🤩', '😇', '🤗', '🦊', '🐱', '🐶', '🐰', '🐻', '🐼', '🦁', '🐸', '🐵', '🌟', '🎯', '🎨', '🎵', '🏆', '💪', '🌈', '🌸', '🍀'];

export const MemberSwitcher: React.FC<MemberSwitcherProps> = ({
  members,
  currentMemberId,
  onSwitch,
  onAddMember,
  onUpdateMember,
  onDeleteMember
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [viewState, setViewState] = useState<'list' | 'add' | 'edit'>('list');

  // Form State
  const [editingMember, setEditingMember] = useState<Member | null>(null);
  const [formName, setFormName] = useState('');
  const [formColor, setFormColor] = useState(COLORS[0]);
  const [formBirthDate, setFormBirthDate] = useState('');
  const [formGender, setFormGender] = useState<'male' | 'female' | ''>('');
  const [formAvatarEmoji, setFormAvatarEmoji] = useState('');
  const [formAvatarImage, setFormAvatarImage] = useState('');
  const [avatarTab, setAvatarTab] = useState<'emoji' | 'upload'>('emoji');
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const currentMember = members.find(m => m.id === currentMemberId) || members[0];

  // Resolve avatar URLs for all members
  const [resolvedAvatarUrls, setResolvedAvatarUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    let active = true;
    const resolveAll = async () => {
      const urls: Record<string, string> = {};
      for (const member of members) {
        if (member.avatarImage && !member.avatarImage.startsWith('data:') && !member.avatarImage.startsWith('http')) {
          const url = await getCheckInImageUrl(member.avatarImage);
          urls[member.id] = url;
        } else if (member.avatarImage) {
          urls[member.id] = member.avatarImage;
        }
      }
      if (active) setResolvedAvatarUrls(urls);
    };
    resolveAll();
    return () => { active = false; };
  }, [members]);

  const getAvatarSrc = (member: Member) => {
    return resolvedAvatarUrls[member.id] || member.avatarImage || '';
  };

  // Reset when closing
  useEffect(() => {
    if (!isOpen) {
      setViewState('list');
      setEditingMember(null);
      setFormName('');
      setPreviewUrl('');
    }
  }, [isOpen]);

  // Resolve avatar image URL
  useEffect(() => {
    let active = true;
    const resolveUrl = async () => {
      if (!formAvatarImage || formAvatarImage.startsWith('data:') || formAvatarImage.startsWith('http')) {
        if (active) setPreviewUrl(formAvatarImage);
        return;
      }
      const url = await getCheckInImageUrl(formAvatarImage);
      if (active) setPreviewUrl(url);
    };
    resolveUrl();
    return () => { active = false; };
  }, [formAvatarImage]);

  const handleOpenAdd = () => {
    setViewState('add');
    setFormName('');
    setFormColor(COLORS[0]);
    setFormBirthDate('');
    setFormGender('');
    setFormAvatarEmoji('😀');
    setFormAvatarImage('');
    setAvatarTab('emoji');
  };

  const handleOpenEdit = (e: React.MouseEvent, member: Member) => {
    e.stopPropagation();
    setEditingMember(member);
    setFormName(member.name);
    setFormColor(member.avatarColor);
    setFormBirthDate(member.birthDate || '');
    setFormGender(member.gender || '');
    setFormAvatarEmoji(member.avatarEmoji || '😀');
    setFormAvatarImage(member.avatarImage || '');
    setPreviewUrl(member.avatarImage || '');
    setAvatarTab(member.avatarImage ? 'upload' : 'emoji');
    setViewState('edit');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formName.trim()) return;

    let finalAvatarImage = avatarTab === 'upload' ? formAvatarImage : undefined;
    let finalAvatarEmoji = avatarTab === 'emoji' ? formAvatarEmoji : undefined;

    // If editing and had a previous storage image that was replaced
    if (viewState === 'edit' && editingMember) {
      const oldImage = editingMember.avatarImage;
      if (oldImage && oldImage !== finalAvatarImage && !oldImage.startsWith('data:') && !oldImage.startsWith('http')) {
        await deleteCheckInImage(oldImage);
      }
    }

    if (viewState === 'add') {
      onAddMember(formName.trim(), formColor, formBirthDate || undefined, formGender || undefined, finalAvatarEmoji, finalAvatarImage);
      setViewState('list');
    } else if (viewState === 'edit' && editingMember) {
      onUpdateMember(editingMember.id, formName.trim(), formColor, formBirthDate || undefined, formGender || undefined, finalAvatarEmoji, finalAvatarImage);
      setViewState('list');
    }
  };

  const handleDelete = () => {
    if (editingMember) {
      onDeleteMember(editingMember.id);
      setViewState('list');
      // If we deleted the current user, the parent component handles the switch, 
      // but we should probably close the modal to avoid UI glitches
      setIsOpen(false);
    }
  };

  return (
    <div className="relative z-50">
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 bg-white dark:bg-slate-800 rounded-full pl-1 pr-3 py-1 shadow-sm border border-slate-100 dark:border-slate-700 hover:shadow-md transition-all active:scale-95"
      >
        <div className={`w-8 h-8 rounded-full ${currentMember?.avatarColor || 'bg-slate-400'} flex items-center justify-center text-white font-bold text-sm overflow-hidden`}>
          {currentMember?.avatarImage ? (
            <img src={getAvatarSrc(currentMember)} alt="" className="w-full h-full object-cover" />
          ) : currentMember?.avatarEmoji ? (
            <span className="text-base">{currentMember.avatarEmoji}</span>
          ) : (
            currentMember?.name.charAt(0).toUpperCase()
          )}
        </div>
        <div className="flex flex-col items-start leading-none">
          <span className="text-sm font-medium text-slate-700 dark:text-slate-200 max-w-[80px] truncate">
            {currentMember?.name}
          </span>
          <span className="text-[10px] items-center gap-0.5 text-amber-500 font-bold flex">
            <Users size={10} className="text-amber-500 fill-amber-500" />
            {currentMember?.totalPoints || 0}
          </span>
        </div>
      </button>


      {/* Dropdown / Modal */}
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute top-12 left-0 w-72 bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-slate-100 dark:border-slate-700 z-50 overflow-hidden animate-fade-in">

            {/* VIEW: LIST */}
            {viewState === 'list' && (
              <>
                <div className="p-3 border-b border-slate-50 dark:border-slate-700">
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-wider px-2 mb-2">切换成员</p>
                  <div className="space-y-1 max-h-60 overflow-y-auto no-scrollbar">
                    {members.map(member => (
                      <div
                        key={member.id}
                        onClick={() => {
                          onSwitch(member.id);
                          setIsOpen(false);
                        }}
                        className={`w-full flex items-center justify-between p-2 rounded-xl transition-colors cursor-pointer group ${currentMemberId === member.id ? 'bg-slate-100 dark:bg-slate-700' : 'hover:bg-slate-50 dark:hover:bg-slate-700/50'}`}
                      >
                        <div className="flex items-center space-x-3 overflow-hidden">
                          <div className={`w-8 h-8 rounded-full flex-shrink-0 ${member.avatarColor} flex items-center justify-center text-xs font-bold overflow-hidden`}>
                            {member.avatarImage ? (
                              <img src={getAvatarSrc(member)} alt="" className="w-full h-full object-cover" />
                            ) : member.avatarEmoji ? (
                              <span className="text-base">{member.avatarEmoji}</span>
                            ) : (
                              <span className="text-white">{member.name.charAt(0)}</span>
                            )}
                          </div>
                          <span className={`text-sm font-medium truncate ${currentMemberId === member.id ? 'text-slate-900 dark:text-white' : 'text-slate-600 dark:text-slate-300'}`}>
                            {member.name}
                          </span>
                        </div>

                        <div className="flex items-center space-x-1">
                          {currentMemberId === member.id && <Check size={16} className="text-primary-500 mr-1" />}
                          <button
                            onClick={(e) => handleOpenEdit(e, member)}
                            className="p-1.5 text-slate-300 hover:text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-full transition-all opacity-0 group-hover:opacity-100"
                          >
                            <Settings size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <button
                  onClick={handleOpenAdd}
                  className="w-full p-3 flex items-center justify-center space-x-2 text-primary-600 dark:text-primary-400 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors text-sm font-medium"
                >
                  <Plus size={16} />
                  <span>添加新成员</span>
                </button>
              </>
            )}

            {/* VIEW: ADD or EDIT */}
            {(viewState === 'add' || viewState === 'edit') && (
              <div className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <button onClick={() => setViewState('list')} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-full text-slate-500">
                      <ArrowLeft size={16} />
                    </button>
                    <h3 className="font-bold text-slate-800 dark:text-white text-sm">
                      {viewState === 'add' ? '新成员' : '编辑成员'}
                    </h3>
                  </div>
                  {viewState === 'edit' && (
                    <button onClick={handleDelete} className="text-red-400 hover:text-red-600 p-1 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors" title="删除成员">
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>

                <form onSubmit={handleSubmit}>
                  <input
                    type="text"
                    placeholder="输入名字"
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm mb-4 focus:ring-2 focus:ring-primary-500 outline-none dark:text-white"
                    autoFocus
                  />

                  <div className="mb-4">
                    <label className="text-xs text-slate-400 mb-2 block">头像</label>
                    {/* Avatar Preview */}
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`w-12 h-12 rounded-full ${formColor} flex items-center justify-center overflow-hidden flex-shrink-0`}>
                        {avatarTab === 'upload' && previewUrl ? (
                          <img src={previewUrl} alt="" className="w-full h-full object-cover" />
                        ) : avatarTab === 'upload' && isUploading ? (
                          <Loader2 size={20} className="animate-spin text-white" />
                        ) : formAvatarEmoji ? (
                          <span className="text-2xl">{formAvatarEmoji}</span>
                        ) : (
                          <span className="text-white text-lg font-bold">{formName ? formName.charAt(0).toUpperCase() : '?'}</span>
                        )}
                      </div>
                      <div className="flex gap-1 bg-slate-100 dark:bg-slate-700 rounded-lg p-0.5">
                        <button
                          type="button"
                          onClick={() => setAvatarTab('emoji')}
                          className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${avatarTab === 'emoji' ? 'bg-white dark:bg-slate-600 shadow-sm text-slate-800 dark:text-white' : 'text-slate-500'}`}
                        >
                          表情
                        </button>
                        <button
                          type="button"
                          onClick={() => setAvatarTab('upload')}
                          className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${avatarTab === 'upload' ? 'bg-white dark:bg-slate-600 shadow-sm text-slate-800 dark:text-white' : 'text-slate-500'}`}
                        >
                          上传
                        </button>
                      </div>
                    </div>

                    {avatarTab === 'emoji' ? (
                      <div className="grid grid-cols-8 gap-1">
                        {AVATAR_EMOJIS.map(emoji => (
                          <button
                            key={emoji}
                            type="button"
                            onClick={() => { setFormAvatarEmoji(emoji); setFormAvatarImage(''); }}
                            className={`w-8 h-8 rounded-lg text-lg flex items-center justify-center transition-all ${formAvatarEmoji === emoji && !formAvatarImage ? 'bg-primary-100 dark:bg-primary-900/30 ring-2 ring-primary-500 scale-110' : 'hover:bg-slate-100 dark:hover:bg-slate-700'}`}
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div>
                        <input
                          type="file"
                          ref={fileInputRef}
                          className="hidden"
                          accept="image/*"
                          onChange={async (e) => {
                            const originalFile = e.target.files?.[0];
                            if (!originalFile) return;

                            const user = await getCurrentUser();
                            if (user) {
                              // Logged in: compress and upload to cloud
                              setIsUploading(true);
                              try {
                                const compressed = await compressImage(originalFile, 500);
                                const { path, error } = await uploadAvatarImage(compressed, user.id);
                                if (error) {
                                  alert("图片上传失败: " + error.message);
                                  setIsUploading(false);
                                  return;
                                }
                                setFormAvatarImage(path);
                                setFormAvatarEmoji('');
                                setPreviewUrl(''); // Will be resolved by useEffect
                              } catch (err) {
                                console.error("Image processing error", err);
                                alert("图片处理失败");
                              } finally {
                                setIsUploading(false);
                              }
                            } else {
                              // Not logged in: use base64 (existing way)
                              const reader = new FileReader();
                              reader.onloadend = () => {
                                setFormAvatarImage(reader.result as string);
                                setFormAvatarEmoji('');
                              };
                              reader.readAsDataURL(originalFile);
                            }
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => !isUploading && fileInputRef.current?.click()}
                          disabled={isUploading}
                          className={`w-full py-2 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg flex items-center justify-center gap-2 text-sm transition-colors ${isUploading ? 'cursor-wait opacity-70 border-primary-500 text-primary-500' : 'text-slate-400 hover:border-primary-500 hover:text-primary-500'}`}
                        >
                          {isUploading ? (
                            <>
                              <Loader2 size={16} className="animate-spin" /> 上传中...
                            </>
                          ) : (
                            <>
                              <Upload size={16} /> 选择图片
                            </>
                          )}
                        </button>
                        {formAvatarImage && (
                          <button
                            type="button"
                            onClick={() => { setFormAvatarImage(''); setFormAvatarEmoji('😀'); setAvatarTab('emoji'); setPreviewUrl(''); }}
                            className="w-full mt-2 py-1 text-xs text-red-500 hover:text-red-600 transition-colors"
                          >
                            移除图片
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="mb-4">
                    <label className="text-xs text-slate-400 mb-2 block">选择背景颜色</label>
                    <div className="flex flex-wrap gap-2">
                      {COLORS.slice(0, 10).map(c => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => setFormColor(c)}
                          className={`w-6 h-6 rounded-full ${c} ${formColor === c ? 'ring-2 ring-offset-1 ring-slate-400' : ''}`}
                        />
                      ))}
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="text-xs text-slate-400 mb-2 block">出生日期（用于健康档案）</label>
                    <input
                      type="date"
                      value={formBirthDate}
                      onChange={(e) => setFormBirthDate(e.target.value)}
                      className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 outline-none dark:text-white"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="text-xs text-slate-400 mb-2 block">性别（用于健康档案）</label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setFormGender('male')}
                        className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${formGender === 'male' ? 'bg-blue-500 text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'}`}
                      >
                        男
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormGender('female')}
                        className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${formGender === 'female' ? 'bg-pink-500 text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'}`}
                      >
                        女
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={!formName.trim()}
                    className="w-full bg-slate-900 dark:bg-white dark:text-slate-900 text-white py-2 rounded-lg text-sm font-bold disabled:opacity-50"
                  >
                    {viewState === 'add' ? '确认添加' : '保存修改'}
                  </button>
                </form>
              </div>
            )}

          </div>
        </>
      )}
    </div>
  );
};